import React, { useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import logoImage from '../assets/images/logo-main.png';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #fff8f4;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Hero = styled.div`
  width: 100%;
  background: linear-gradient(135deg, #ffe5d0 0%, #fff8f4 100%);
  color: #ff6b3d;
  padding: 28px 0 56px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 20px 0 40px 0;
  }
  @media (max-width: 480px) {
    padding: 16px 0 28px 0;
  }
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto 6px auto;
  position: relative;
  min-height: 56px;
  padding: 0 16px;
`;

const CenteredTitle = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  pointer-events: none;
  @media (max-width: 480px) {
    position: relative;
    margin-top: 16px;
    transform: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.3rem;
  font-weight: 800;
  color: #ff6b3d;
  margin: 0;
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.15rem;
  color: #b85c2b;
  max-width: 600px;
  padding: 0 16px;
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 500px;
  }
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  max-width: 1100px;
  margin-top: 18px;
  padding: 0 16px 48px 16px;
  gap: 40px;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    margin-top: 0;
    gap: 24px;
  }
  @media (max-width: 480px) {
    padding: 0 12px 32px 12px;
    gap: 12px;
  }
`;

const TOCSidebar = styled.nav`
  min-width: 210px;
  margin-right: 0;
  background: #fff7f2;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(255,107,61,0.04);
  padding: 24px 14px;
  font-size: 1rem;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: sticky;
  top: 32px;
  a {
    color: #ff8a4c;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    border-left: 3px solid transparent;
    padding: 8px 0 8px 12px;
    border-radius: 6px;
    transition: color 0.2s, border-color 0.2s, background 0.2s;
    &:hover {
      color: #b85c2b;
      background: #ffe5d0;
      border-left: 3px solid #ff8a4c;
    }
    &.active {
      color: #fff;
      background: #ff8a4c;
      border-left: 3px solid #ff6b3d;
    }
  }
  @media (max-width: 900px) {
    flex-direction: row;
    flex-wrap: wrap;
    min-width: 0;
    width: 100%;
    margin: 0 0 16px 0;
    padding: 12px 8px;
    justify-content: center;
    position: static;
    background: #fff7f2;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(255,107,61,0.04);
    gap: 8px;
    a {
      padding: 8px 10px;
      border-radius: 6px;
      font-size: 0.9rem;
      border-left: none;
      text-align: center;
    }
  }
  @media (max-width: 480px) {
    flex-direction: column;
    padding: 12px 10px;
    gap: 4px;
    a {
      width: 100%;
      font-size: 0.95rem;
      padding: 10px;
      text-align: left;
      border-left: 3px solid transparent;
      margin-bottom: 2px;
      &:hover {
        border-left: 3px solid #ff8a4c;
      }
    }
  }
`;

const Card = styled.div`
  background: var(--white);
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(255,107,61,0.06);
  padding: 40px 36px 36px 0;
  max-width: 750px;
  width: 100%;
  color: var(--text-color);
  animation: ${fadeInUp} 0.7s cubic-bezier(0.23, 1, 0.32, 1);
  z-index: 2;
  position: relative;
  display: flex;
  flex-direction: row;
  @media (max-width: 900px) {
    padding: 24px 16px 24px 0;
    max-width: 100%;
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
  @media (max-width: 480px) {
    padding: 16px 12px 20px 0;
    border-radius: 8px;
    margin-top: 8px;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  background: linear-gradient(180deg, #ffe5d0 0%, #ffb88c 100%);
  border-radius: 6px 0 0 6px;
  margin-right: 28px;
  @media (max-width: 768px) {
    width: 100%;
    height: 4px;
    border-radius: 6px 6px 0 0;
    margin: 0 0 18px 0;
  }
`;

const CardContent = styled.div`
  flex: 1;
  padding-right: 0;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
  @media (max-width: 480px) {
    padding: 0 8px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-bottom: 0;
  margin-left: 8px;
  z-index: 10;
  img {
    height: 48px;
    width: auto;
    object-fit: contain;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.8;
    }
    @media (max-width: 480px) {
      height: 40px;
    }
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.35rem;
  color: #ff6b3d;
  margin-top: 36px;
  margin-bottom: 14px;
  font-weight: 800;
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-top: 28px;
  }
  @media (max-width: 480px) {
    font-size: 1.15rem;
    margin-top: 24px;
    margin-bottom: 12px;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  margin-bottom: 24px;
  @media (max-width: 480px) {
    margin-left: 16px;
    margin-bottom: 18px;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 18px;
  color: #3d2c1e;
  line-height: 1.7;
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 14px;
    line-height: 1.6;
  }
`;

const BackToTop = styled.button`
  position: fixed;
  bottom: 32px;
  right: 32px;
  background: #ff8a4c;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 1.5rem;
  box-shadow: 0 4px 16px rgba(255,107,61,0.15);
  cursor: pointer;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover {
    background: #ff6b3d;
  }
  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    font-size: 1.3rem;
    bottom: 24px;
    right: 24px;
  }
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
    bottom: 16px;
    right: 16px;
  }
`;

const PrivacyPolicy: React.FC = () => {
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <PageWrapper>
      <div ref={topRef} />
      <Hero>
        <HeroRow>
          <LogoLink to="/" style={{ marginBottom: 0 }}>
            <img src={logoImage} alt="WhatzPro Home" />
          </LogoLink>
          <CenteredTitle>
            <HeroTitle>Privacy Policy</HeroTitle>
          </CenteredTitle>
          <div style={{ width: 48 }} /> {/* Spacer for symmetry, matches logo width */}
        </HeroRow>
        <HeroSubtitle>
          At WhatzPro, we value your privacy. This policy explains how we collect, use, and protect your personal data when you use our services.
        </HeroSubtitle>
      </Hero>
      <ContentArea>
        <TOCSidebar>
          <a onClick={() => scrollTo('info-collection')}>Information We Collect</a>
          <a onClick={() => scrollTo('info-use')}>How We Use Your Data</a>
          <a onClick={() => scrollTo('info-sharing')}>Sharing Your Data</a>
          <a onClick={() => scrollTo('data-security')}>Data Security & Retention</a>
          <a onClick={() => scrollTo('children')}>Children's Privacy</a>
          <a onClick={() => scrollTo('third-party')}>Third-Party Links</a>
          <a onClick={() => scrollTo('updates')}>Updates</a>
          <Link to="/" onClick={() => {
            setTimeout(() => {
              const contactElement = document.getElementById('contact');
              if (contactElement) {
                contactElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }} style={{
            color: '#ff8a4c',
            fontWeight: 600,
            textDecoration: 'none',
            borderLeft: '3px solid transparent',
            padding: '8px 0 8px 12px',
            borderRadius: '6px'
          }}>Contact Us</Link>
        </TOCSidebar>
        <Card>
          <AccentBar />
          <CardContent>
            <SectionHeading id="info-collection">Information We Collect</SectionHeading>
            <Paragraph><strong>Personal Data:</strong> Name, email, phone number, and other contact details.</Paragraph>
            <Paragraph><strong>Usage Data:</strong> IP address, browser type, pages visited, time spent, and device info.</Paragraph>
            <Paragraph><strong>Cookies:</strong> We use cookies to enhance your experience. You can control cookie settings in your browser.</Paragraph>

            <SectionHeading id="info-use">How We Use Your Data</SectionHeading>
            <Paragraph>We use your data to:</Paragraph>
            <List>
              <li>Provide and improve our services</li>
              <li>Manage your account</li>
              <li>Communicate with you</li>
              <li>Analyze usage trends</li>
              <li>Send updates, offers, and relevant content (if you've opted in)</li>
            </List>

            <SectionHeading id="info-sharing">Sharing Your Data</SectionHeading>
            <Paragraph>We may share your information with:</Paragraph>
            <List>
              <li>Service providers helping us run our platform</li>
              <li>Business partners (with your consent)</li>
              <li>Legal authorities when required by law</li>
            </List>

            <SectionHeading id="data-security">Data Security & Retention</SectionHeading>
            <Paragraph>We take reasonable steps to protect your data. Your data is stored securely and retained only as long as necessary for the purposes stated here.</Paragraph>

            <SectionHeading id="children">Children's Privacy</SectionHeading>
            <Paragraph>Our services are not intended for children under 13. We do not knowingly collect data from minors.</Paragraph>

            <SectionHeading id="third-party">Third-Party Links</SectionHeading>
            <Paragraph>We may link to external websites. We are not responsible for their privacy practices.</Paragraph>

            <SectionHeading id="updates">Updates</SectionHeading>
            <Paragraph>We may update this policy from time to time. Please review it periodically.</Paragraph>

            <SectionHeading id="contact-us">Get in Touch</SectionHeading>
            <Paragraph>For any questions about our privacy practices, please <Link to="/" onClick={() => {
              setTimeout(() => {
                const contactElement = document.getElementById('contact');
                if (contactElement) {
                  contactElement.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }} style={{ color: '#ff6b3d', textDecoration: 'underline' }}>contact us</Link> or email directly at: <a href="mailto:info@theonetechnologies.com" style={{ color: '#ff6b3d', textDecoration: 'underline' }}>info@theonetechnologies.com</a></Paragraph>
          </CardContent>
        </Card>
      </ContentArea>
      <BackToTop onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })} title="Back to top">↑</BackToTop>
    </PageWrapper>
  );
};

export default PrivacyPolicy; 